import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ShieldCheck, Users, Wallet, Server, Receipt, Activity,
  Loader2, RefreshCw, ArrowUpRight, LogOut, ExternalLink, CheckCircle2,
  PauseCircle, XCircle,
} from "lucide-react";

const TABS = [
  { key: "overview",      label: "OVERVIEW",      Icon: Activity },
  { key: "users",         label: "USERS",         Icon: Users },
  { key: "payments",      label: "PAYMENTS",      Icon: Receipt },
  { key: "subscriptions", label: "SUBSCRIPTIONS", Icon: Server },
  { key: "ledger",        label: "BALANCE LEDGER",Icon: Wallet },
  { key: "logs",          label: "SYSTEM LOGS",   Icon: ShieldCheck },
];

// ---------------- helpers ----------------

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function Stat({ label, value, sub, accent = false }) {
  return (
    <div className={`card-flat p-5 ${accent ? "border-signal/60" : ""}`}>
      <div className="overline">{label}</div>
      <div className={`mt-3 font-mono text-3xl ${accent ? "text-signal" : "text-white"}`}>
        {value}
      </div>
      {sub && <div className="mt-2 font-mono text-[11px] text-zinc-500 uppercase tracking-widest">{sub}</div>}
    </div>
  );
}

function Loader() {
  return (
    <div className="flex items-center gap-2 text-zinc-500">
      <Loader2 size={14} className="animate-spin" />
      <span className="font-mono text-xs uppercase tracking-widest">LOADING…</span>
    </div>
  );
}

function Table({ cols, rows, emptyText = "// no rows" }) {
  return (
    <div className="card-flat overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-800">
            {cols.map((c) => (
              <th key={c} className="text-left px-4 py-3 overline font-normal whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td className="px-4 py-6 text-zinc-500 font-mono text-xs" colSpan={cols.length}>{emptyText}</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-zinc-800/60 last:border-0 hover:bg-zinc-900/40">
              {r.map((c, j) => (
                <td key={j} className="px-4 py-3 align-top text-zinc-200 whitespace-nowrap">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------- overview ----------------

function OverviewTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    setErr("");
    try {
      const o = await api.get("/admin/overview");
      setData(o.data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  if (err) return <div className="font-mono text-sm text-red-300">// {err}</div>;
  if (!data) return <Loader />;

  return (
    <div data-testid="admin-overview">
      <div className="flex items-center justify-between mb-6">
        <div className="overline">// LIVE METRICS · AUTO-REFRESH 30s</div>
        <button onClick={load} disabled={refreshing} className="btn-ghost h-9 px-3 text-xs font-mono tracking-widest disabled:opacity-50">
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> REFRESH
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="TOTAL USERS" value={data.users.total} sub={`${data.users.admins} ADMIN(S)`} accent />
        <Stat
          label="ACTIVE SUBSCRIPTIONS"
          value={data.subscriptions.active}
          sub={`${data.subscriptions.suspended} SUSPENDED · ${data.subscriptions.cancelled} CANCELLED`}
        />
        <Stat
          label="OUTSTANDING BALANCE"
          value={`$${data.balances.outstanding_usd.toFixed(2)}`}
          sub="UNSPENT CUSTOMER CREDIT"
        />
        <Stat
          label="SUCCESSFUL TOP-UPS"
          value={data.topups.successful_count}
          sub={`LIFETIME $${data.balances.lifetime_topped_up_usd.toFixed(2)}`}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="REVENUE 24h" value={`$${data.topups.revenue_24h_usd.toFixed(2)}`} />
        <Stat label="REVENUE 7d" value={`$${data.topups.revenue_7d_usd.toFixed(2)}`} />
        <Stat label="REVENUE 30d" value={`$${data.topups.revenue_30d_usd.toFixed(2)}`} />
        <Stat label="LIFETIME CONSUMED" value={`$${data.balances.lifetime_consumed_usd.toFixed(2)}`} />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-flat p-6">
          <div className="overline">// BALANCE POOL (CloudRent → Customers)</div>
          <div className="mt-4 space-y-3 font-mono text-sm">
            <div className="flex justify-between border-b border-zinc-800 pb-2">
              <span className="text-zinc-400">Outstanding (unspent customer credit)</span>
              <span className="text-white">${data.balances.outstanding_usd.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-b border-zinc-800 pb-2">
              <span className="text-zinc-400">Lifetime topped up</span>
              <span className="text-white">${data.balances.lifetime_topped_up_usd.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Lifetime consumed</span>
              <span className="text-white">${data.balances.lifetime_consumed_usd.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="card-flat p-6">
          <div className="overline">// PAYMENT ACTIVITY</div>
          <div className="mt-4 space-y-3 font-mono text-sm">
            <div className="flex justify-between border-b border-zinc-800 pb-2">
              <span className="text-zinc-400">Successful top-ups (credited)</span>
              <span className="text-white">{data.topups.successful_count}</span>
            </div>
            <div className="flex justify-between border-b border-zinc-800 pb-2">
              <span className="text-zinc-400">Last successful payment</span>
              <span className="text-white">{fmtDate(data.last_payment_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Metrics generated</span>
              <span className="text-white">{fmtDate(data.generated_at)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 font-mono text-[11px] text-zinc-500 uppercase tracking-widest">
        // All numbers above are queried live from MongoDB Atlas — the source of
        truth for users, subscriptions, balances and audit logs.
      </div>
    </div>
  );
}

// ---------------- users ----------------

function UsersTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/admin/users").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);
  if (err) return <div className="font-mono text-sm text-red-300">// {err}</div>;
  if (!data) return <Loader />;

  return (
    <div data-testid="admin-users">
      <div className="overline mb-4">// {data.total} USERS</div>
      <Table
        cols={["EMAIL", "NAME", "ROLE", "BALANCE", "TOPPED UP", "CONSUMED", "ACTIVE SUBS", "TOPUPS", "JOINED"]}
        rows={data.items.map((u) => [
          u.email,
          u.name || "—",
          <span className={`badge-mono ${u.role === "admin" ? "border-signal/60 text-signal" : ""}`}>{u.role.toUpperCase()}</span>,
          `$${u.balance_usd.toFixed(2)}`,
          `$${u.total_topped_up_usd.toFixed(2)}`,
          `$${u.total_consumed_usd.toFixed(2)}`,
          u.active_subscriptions,
          u.paid_topups,
          fmtDate(u.created_at),
        ])}
      />
    </div>
  );
}

// ---------------- payments (our DB) ----------------

function PaymentsTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/admin/payments?limit=100").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);
  if (err) return <div className="font-mono text-sm text-red-300">// {err}</div>;
  if (!data) return <Loader />;

  return (
    <div data-testid="admin-payments">
      <div className="overline mb-4">// {data.total} PAYMENT TRANSACTIONS · CloudRent DB</div>
      <Table
        cols={["SESSION ID", "USER", "TYPE", "AMOUNT", "CREDIT", "STATUS", "PAY STATUS", "CREATED"]}
        rows={data.items.map((p) => [
          <span className="font-mono text-[11px] text-zinc-300 break-all" title={p.session_id}>
            {p.session_id?.slice(0, 14)}…
          </span>,
          p.user_email,
          p.type?.toUpperCase(),
          `$${(p.amount || 0).toFixed(2)}`,
          p.credit_amount ? `$${p.credit_amount.toFixed(2)}` : "—",
          <span className={`badge-mono ${p.status === "complete" ? "border-signal/60 text-signal" : ""}`}>
            {(p.status || "—").toUpperCase()}
          </span>,
          <span className={`badge-mono ${p.payment_status === "paid" ? "border-signal/60 text-signal" : ""}`}>
            {(p.payment_status || "—").toUpperCase()}
          </span>,
          fmtDate(p.created_at),
        ])}
      />
    </div>
  );
}

// ---------------- subscriptions ----------------

const SUB_STATUS = {
  active:    { Icon: CheckCircle2, color: "border-signal/60 text-signal" },
  suspended: { Icon: PauseCircle,  color: "border-amber-700 text-amber-400" },
  cancelled: { Icon: XCircle,      color: "border-red-900 text-red-400" },
};

function SubscriptionsTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/admin/subscriptions?limit=100").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);
  if (err) return <div className="font-mono text-sm text-red-300">// {err}</div>;
  if (!data) return <Loader />;

  return (
    <div data-testid="admin-subscriptions">
      <div className="overline mb-4">// {data.total} SUBSCRIPTIONS</div>
      <Table
        cols={["USER", "SERVICE", "PLAN", "DAILY", "STATUS", "HOST", "PROVISIONED", "NEXT BILL"]}
        rows={data.items.map((s) => {
          const st = SUB_STATUS[s.status] || SUB_STATUS.active;
          return [
            s.user_email,
            s.service_name,
            s.plan_name,
            `$${s.daily_cost_usd.toFixed(2)}/d`,
            <span className={`badge-mono ${st.color}`}>
              <st.Icon size={10} className="mr-1" /> {s.status?.toUpperCase()}
            </span>,
            <span className="font-mono text-[11px] text-zinc-300">{s.delivery?.server_host || "—"}</span>,
            fmtDate(s.provisioned_at),
            fmtDate(s.next_billing_at),
          ];
        })}
      />
    </div>
  );
}

// ---------------- balance ledger ----------------

function LedgerTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/admin/ledger?limit=100").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);
  if (err) return <div className="font-mono text-sm text-red-300">// {err}</div>;
  if (!data) return <Loader />;

  return (
    <div data-testid="admin-ledger">
      <div className="overline mb-4">// {data.total} BALANCE TRANSACTIONS (every credit / deduction)</div>
      <Table
        cols={["DATE", "USER", "TYPE", "AMOUNT", "BALANCE AFTER", "DESCRIPTION"]}
        rows={data.items.map((t) => [
          fmtDate(t.created_at),
          t.user_email || t.user_id,
          <span className={`badge-mono ${t.type === "topup" ? "border-signal/60 text-signal" : ""}`}>
            {(t.type || "").toUpperCase()}
          </span>,
          <span className={`font-mono ${t.amount > 0 ? "text-signal" : "text-zinc-200"}`}>
            {t.amount > 0 ? "+" : ""}${t.amount.toFixed(2)}
          </span>,
          `$${(t.balance_after || 0).toFixed(2)}`,
          t.description,
        ])}
      />
    </div>
  );
}

// ---------------- system logs (terminal style) ----------------

function LogsTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = () =>
    api.get("/admin/logs?limit=200").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));

  useEffect(() => {
    load();
    const id = setInterval(load, 10_000);
    return () => clearInterval(id);
  }, []);

  if (err) return <div className="font-mono text-sm text-red-300">// {err}</div>;
  if (!data) return <Loader />;

  const colorFor = (level) => {
    const l = (level || "").toLowerCase();
    if (l === "error") return "text-red-400";
    if (l === "warning") return "text-amber-400";
    if (l === "info") return "text-zinc-300";
    return "text-zinc-400";
  };

  return (
    <div data-testid="admin-logs">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">// PAYMENT_LOGS · {data.count} ENTRIES · AUTO-REFRESH 10s</div>
        <button onClick={load} className="btn-ghost h-8 px-3 text-[11px] font-mono tracking-widest">
          <RefreshCw size={11} /> REFRESH
        </button>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="flex items-center gap-1.5 border-b border-zinc-800 px-3 py-2">
          <span className="h-2 w-2 rounded-full bg-zinc-700" />
          <span className="h-2 w-2 rounded-full bg-zinc-700" />
          <span className="h-2 w-2 rounded-full bg-zinc-700" />
          <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            tail -f /var/log/cloudrent/payment.log
          </span>
        </div>
        <div className="font-mono text-[12px] leading-6 p-4 max-h-[60vh] overflow-y-auto bg-black/40">
          {data.items.length === 0 && (
            <div className="text-zinc-500">// no log entries yet</div>
          )}
          {data.items.map((l, i) => (
            <div key={i} className="whitespace-pre-wrap break-words">
              <span className="text-zinc-500">[{fmtDate(l.created_at)}]</span>{" "}
              <span className={`${colorFor(l.level)} font-semibold`}>[{(l.level || "info").toUpperCase()}]</span>{" "}
              <span className="text-signal">order={l.order_id?.slice(0, 8)}</span>{" "}
              <span className="text-white">{l.message}</span>
              {l.meta && Object.keys(l.meta).length > 0 && (
                <span className="text-zinc-500"> {JSON.stringify(l.meta)}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 font-mono text-[11px] text-zinc-500 uppercase tracking-widest">
        // Logs are stored in MongoDB `payment_logs` collection, written by our
        payment-bridge routes on every checkout, webhook and provisioning event.
      </div>
    </div>
  );
}

// ---------------- main page ----------------

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  // Defence in depth — backend already enforces this, but redirect non-admins early
  useEffect(() => {
    if (user === false) {
      nav(`/login?next=${encodeURIComponent("/admin")}`);
    } else if (user && user.role !== "admin") {
      nav("/dashboard", { replace: true });
    }
  }, [user, nav]);

  if (!user || user.role !== "admin") {
    return (
      <div className="container-pad py-24 font-mono text-xs uppercase tracking-widest text-zinc-500">
        CHECKING ADMIN ACCESS…
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen">
      {/* Top bar */}
      <div className="border-b border-zinc-800 bg-black/80 backdrop-blur-xl sticky top-16 z-40">
        <div className="container-pad flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <ShieldCheck size={16} className="text-signal" />
            <div className="font-display text-lg tracking-tight">
              Admin <span className="text-signal">Panel</span>
            </div>
            <span className="badge-mono">CLOUDRENT</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] text-zinc-500 uppercase tracking-widest hidden md:inline">
              Signed in as <span className="text-zinc-200">{user.email}</span>
            </span>
            <button
              onClick={async () => { await logout(); nav("/"); }}
              data-testid="admin-logout"
              className="btn-ghost h-8 px-3 text-[11px] font-mono tracking-widest"
            >
              <LogOut size={11} /> LOGOUT
            </button>
          </div>
        </div>
      </div>

      <div className="container-pad py-8 grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Sidebar */}
        <aside className="md:col-span-3 lg:col-span-2">
          <nav className="card-flat p-2 sticky top-32">
            <div className="overline px-2 py-1.5">// SECTIONS</div>
            {TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={`admin-tab-${key}`}
                className={`w-full flex items-center gap-2 px-3 py-2 mt-1 rounded-sm font-mono text-[11px] tracking-widest transition-colors ${
                  tab === key
                    ? "bg-zinc-900 text-white border-l-2 border-signal"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <Icon size={13} />
                <span>{label}</span>
              </button>
            ))}

            <div className="mt-4 border-t border-zinc-800 pt-3 px-2">
              <div className="overline mb-2">// LINKS</div>
              <Link to="/dashboard" className="font-mono text-[11px] text-zinc-400 hover:text-white flex items-center gap-1.5">
                CUSTOMER DASHBOARD <ArrowUpRight size={10} />
              </Link>
              <Link to="/catalog" className="font-mono text-[11px] text-zinc-400 hover:text-white flex items-center gap-1.5 mt-1.5">
                PUBLIC CATALOG <ArrowUpRight size={10} />
              </Link>
            </div>
          </nav>
        </aside>

        {/* Main panel */}
        <main className="md:col-span-9 lg:col-span-10">
          {tab === "overview"      && <OverviewTab />}
          {tab === "users"         && <UsersTab />}
          {tab === "payments"      && <PaymentsTab />}
          {tab === "subscriptions" && <SubscriptionsTab />}
          {tab === "ledger"        && <LedgerTab />}
          {tab === "logs"          && <LogsTab />}
        </main>
      </div>
    </div>
  );
}
